import L from 'leaflet';

// An area clicked on or off changes look in step with its homes (both views). Turned off, its homes shrink
// into dots and vanish first (listingGroupAnimation.js), then the area fades to grey or into the dimming;
// turned on, its colour fades in first, then the homes grow in.
//
// A click notes the toggle (noteAreaToggle) before the view's state changes. A toggle that takes homes off
// the map is held: the area keeps its old look until the views' marker update says the homes are going
// (releaseHeldAreaFades, with the shrink's length) or, failing that, a fallback timeout. Any other toggle
// fades at once, and listingsGrowDelay() tells the marker animation how long the new homes should wait.
// The area itself fades through fadeAreaLayer (a Leaflet path) and the selection overlay's patches
// (areaSelectionOverlay.js).
//
// The filter drawer's search button does the same for every area at once (noteFiltersApplied +
// fadeAreaLayers): one toggle that stands for all areas, so whatever the new filters grey out or bring back
// fades together, after the homes going away have shrunk and before the new ones grow in.
//
// Areas a search adds (areaSelectionOverlay.js) wait, still dimmed and with their homes left off the map
// (isAreaRevealPending), until the map has stopped flying there; then they light up one after another and
// noteAreaReveal tells the homes how long to wait. Areas a search drops get dimmed after their homes have
// shrunk away (noteSelectionHold).

export const AREA_TOGGLE_FADE_MS = 300;
// A held fade starts after this even if no homes went (listvy's request can be slow).
const HOLD_FALLBACK_MS = 2500;
// A toggle older than this no longer animates anything.
const TOGGLE_TTL_MS = 3000;
// The drawer's search: its fades wait at most this long for the homes (an unchanged result never says).
const APPLY_HOLD_FALLBACK_MS = 1200;
// The toggle that stands for every area (noteFiltersApplied).
const ALL_AREAS = '*';

const toggles = new Map();
// The last time held toggles were released, so a hold noted just after (an effect that ran later in the same
// update) doesn't wait for a release that already happened.
let lastRelease = { at: -Infinity, shrinkMs: 0 };

const fire = (record) => {
    if (record.fired) return;
    record.fired = true;
    if (record.timer) clearTimeout(record.timer);
    record.timer = null;
    const callbacks = record.callbacks.splice(0);
    callbacks.forEach((callback) => callback());
};

const release = (record, delayMs) => {
    if (record.released) return;
    record.released = true;
    if (record.timer) clearTimeout(record.timer);
    record.readyAt = performance.now() + delayMs;
    record.timer = delayMs > 0 ? setTimeout(() => fire(record), delayMs) : null;
    if (delayMs <= 0) fire(record);
};

const createToggle = (code, hold, fallbackMs) => {
    const previous = toggles.get(code);
    if (previous) fire(previous);
    const now = performance.now();
    const record = {
        hold,
        createdAt: now,
        readyAt: hold ? null : now,
        released: !hold,
        fired: !hold,
        callbacks: [],
        timer: null,
    };
    if (hold) {
        record.timer = setTimeout(() => release(record, 0), fallbackMs);
        if (now - lastRelease.at < 100) release(record, Math.max(0, lastRelease.at + (lastRelease.shrinkMs * 0.8) - now));
    }
    toggles.set(code, record);
    return record;
};

// hold: the area has homes on the map that this click takes away.
export const noteAreaToggle = (regsoCode, { hold = false } = {}) => {
    const code = String(regsoCode || '').trim();
    if (!code) return null;
    return createToggle(code, hold, HOLD_FALLBACK_MS);
};

// The drawer's search button: every area may change. hold: homes are on the map (some may go away).
export const noteFiltersApplied = ({ hold = false } = {}) => createToggle(ALL_AREAS, hold, APPLY_HOLD_FALLBACK_MS);

const liveToggle = (code) => {
    const record = toggles.get(code);
    if (!record) return null;
    if (performance.now() - record.createdAt > TOGGLE_TTL_MS) {
        toggles.delete(code);
        return null;
    }
    return record;
};

// The toggle of this area from the last few seconds (its own click, or the drawer's search), if any.
export const areaToggleFor = (regsoCode) => {
    const code = String(regsoCode || '').trim();
    return (code && liveToggle(code)) || liveToggle(ALL_AREAS);
};

// Runs callback once the area may change look (at once for a toggle that isn't held, or no toggle).
export const whenAreaFadeReady = (regsoCode, callback) => {
    const record = areaToggleFor(regsoCode);
    if (!record || record.fired) callback();
    else record.callbacks.push(callback);
};

export const isAreaFadeHeld = (regsoCode) => {
    const record = areaToggleFor(regsoCode);
    return Boolean(record && !record.fired);
};

// The views' marker update ran: held areas fade once the homes have shrunk (shrinkMs, 0 when none animate).
// The shrink eases out, so its last fifth hardly shows: the fade starts there.
export const releaseHeldAreaFades = (shrinkMs = 0) => {
    lastRelease = { at: performance.now(), shrinkMs };
    toggles.forEach((record) => {
        if (record.hold && !record.released) release(record, Math.round(shrinkMs * 0.8));
    });
};

// How long homes appearing now should wait: until the areas changing have faded (a held toggle once it's
// released, i.e. after the homes going away have shrunk).
export const listingsGrowDelay = () => {
    const now = performance.now();
    let delay = 0;
    toggles.forEach((record) => {
        if (record.readyAt == null || now - record.createdAt > TOGGLE_TTL_MS) return;
        delay = Math.max(delay, record.readyAt + AREA_TOGGLE_FADE_MS - now);
    });
    return Math.max(0, delay);
};

// Areas joining the selection through a search, not yet lit up: their homes stay off the map meanwhile.
const revealPending = new Set();
const revealListeners = new Set();

export const isAreaRevealPending = (regsoCode) => revealPending.size > 0 && revealPending.has(String(regsoCode || '').trim());

export const setAreasRevealPending = (codes, pending) => {
    let changed = false;
    codes.forEach((code) => {
        const id = String(code || '').trim();
        if (!id || revealPending.has(id) === pending) return;
        if (pending) revealPending.add(id);
        else revealPending.delete(id);
        changed = true;
    });
    if (changed) revealListeners.forEach((listener) => listener());
};

// Called when areas stop (or start) waiting; returns the unsubscribe.
export const onAreaRevealChange = (listener) => {
    revealListeners.add(listener);
    return () => revealListeners.delete(listener);
};

// Reveal-colour: while a search's bigger reveal propagates (areas light up, listings grow), the tiles in
// the revealed scope show their full metric colour regardless of the Färger toggle. When the reveal
// finishes, endRevealColour fires so the views settle the tiles to the toggle — fading the colour away
// when it's lines-only or off, and leaving it when the toggle is the full colours (Fyllda). The views read
// isRevealColourForced() in their tile styling.
let revealColourActive = false;
let revealColourCodes = new Set();
const revealColourEndListeners = new Set();

export const beginRevealColour = (codes) => {
    revealColourCodes = new Set((codes || []).map((code) => String(code || '').trim()).filter(Boolean));
    revealColourActive = revealColourCodes.size > 0;
};
export const isRevealColourForced = (regsoCode) =>
    revealColourActive && revealColourCodes.has(String(regsoCode || '').trim());
// Clears the scope and tells the views to settle the tiles to the current Färger toggle (with a fade).
export const endRevealColour = () => {
    if (!revealColourActive) return;
    revealColourActive = false;
    const codes = [...revealColourCodes];
    revealColourCodes = new Set();
    revealColourEndListeners.forEach((listener) => listener(codes));
};
export const onRevealColourEnd = (listener) => {
    revealColourEndListeners.add(listener);
    return () => revealColourEndListeners.delete(listener);
};

// Areas are lighting up one after another over lastStartMs (the last one starts then and fades for
// AREA_TOGGLE_FADE_MS): homes appearing meanwhile grow in after it.
export const noteAreaReveal = (lastStartMs) => {
    const record = createToggle('~reveal', false, 0);
    record.readyAt = performance.now() + lastStartMs;
    return record;
};

// Per area, when its homes go (a delay from the start of the shrink) or come (a time), so homes follow their
// area in a staggered search change instead of all going or growing at once. Read once by the marker
// animation; old entries are ignored.
const shrinkSchedule = new Map();
const growSchedule = new Map();
const SCHEDULE_TTL_MS = 4000;
const scheduled = (schedule, regsoCode) => {
    const entry = schedule.get(String(regsoCode || '').trim());
    if (!entry) return null;
    if (performance.now() - entry.noted > SCHEDULE_TTL_MS) return null;
    return entry.value;
};
// codes[i]'s homes start shrinking delays[i] ms after the homes start going.
export const setAreaShrinkSchedule = (codes, delays) => {
    const noted = performance.now();
    codes.forEach((code, index) => shrinkSchedule.set(code, { noted, value: delays[index] || 0 }));
};
// codes[i]'s homes start growing at times[i] (performance.now() time).
export const setAreaGrowSchedule = (codes, times) => {
    const noted = performance.now();
    codes.forEach((code, index) => growSchedule.set(code, { noted, value: times[index] }));
};
export const areaShrinkDelay = (regsoCode) => scheduled(shrinkSchedule, regsoCode);
export const areaGrowAt = (regsoCode) => scheduled(growSchedule, regsoCode);

// Areas a search drops: their dimming waits (onReady) until their homes have shrunk away, when there are any.
export const noteSelectionHold = ({ hold = false } = {}, onReady) => {
    const record = createToggle('~selection', hold, HOLD_FALLBACK_MS);
    if (record.fired) onReady();
    else record.callbacks.push(onReady);
    return record;
};

// Whether the view has homes of an area on the map (registered by the view that's showing).
let listingsShownIn = () => false;
export const setListingsShownCheck = (check) => {
    listingsShownIn = check || (() => false);
};
export const anyListingsShown = (codes) => codes.some((code) => listingsShownIn(String(code || '').trim()));

export const easeAreaFade = (t) => (t < 0.5 ? 2 * t * t : 1 - (((-2 * t) + 2) ** 2) / 2);

// Colours as [r, g, b, a] (the browser parses names, hex and rgba).
const parseContext = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
const parseCache = new Map();
const parseColor = (color) => {
    if (!color || color === 'transparent' || !parseContext) return null;
    if (parseCache.has(color)) return parseCache.get(color);
    parseContext.fillStyle = '#000000';
    parseContext.fillStyle = color;
    const value = parseContext.fillStyle;
    let rgba;
    if (value[0] === '#') {
        rgba = [parseInt(value.slice(1, 3), 16), parseInt(value.slice(3, 5), 16), parseInt(value.slice(5, 7), 16), 1];
    } else {
        const parts = value.match(/[\d.]+/g) || [0, 0, 0, 0];
        rgba = [Number(parts[0]), Number(parts[1]), Number(parts[2]), parts[3] != null ? Number(parts[3]) : 1];
    }
    parseCache.set(color, rgba);
    return rgba;
};

// A colour with its opacity folded in; off (no fill / no stroke) is fully transparent.
const paint = (color, opacity, on) => {
    const rgba = parseColor(color);
    if (!rgba || !on) return { rgb: rgba ? rgba.slice(0, 3) : null, alpha: 0 };
    return { rgb: rgba.slice(0, 3), alpha: rgba[3] * (opacity ?? 1) };
};

const mixPaint = (from, to, t) => {
    const fromRgb = from.rgb || to.rgb || [0, 0, 0];
    const toRgb = to.rgb || from.rgb || [0, 0, 0];
    const rgb = fromRgb.map((value, index) => Math.round(value + ((toRgb[index] - value) * t)));
    return { color: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`, alpha: from.alpha + ((to.alpha - from.alpha) * t) };
};

const STYLE_KEYS = ['fillColor', 'fillOpacity', 'color', 'opacity', 'weight', 'stroke', 'fill'];
const pickStyle = (options) => {
    const style = {};
    STYLE_KEYS.forEach((key) => { style[key] = options[key]; });
    return style;
};

// The in-between look of a path at t (0 = from, 1 = to).
const mixStyle = (from, to, t) => {
    const fill = mixPaint(
        paint(from.fillColor, from.fillOpacity, from.fill !== false),
        paint(to.fillColor, to.fillOpacity, to.fill !== false),
        t,
    );
    const fromStroke = from.stroke !== false;
    const toStroke = to.stroke !== false;
    const stroke = mixPaint(paint(from.color, from.opacity, fromStroke), paint(to.color, to.opacity, toStroke), t);
    const fromWeight = fromStroke ? (from.weight ?? 1) : (to.weight ?? 1);
    const toWeight = toStroke ? (to.weight ?? 1) : fromWeight;
    return {
        fill: true,
        fillColor: fill.color,
        fillOpacity: fill.alpha,
        stroke: fromStroke || toStroke,
        color: stroke.color,
        opacity: stroke.alpha,
        weight: fromWeight + ((toWeight - fromWeight) * t),
    };
};

// Spreading a batch of area fades over time so they don't all go at once: the same feel as the reveal's
// own stagger (areaSelectionOverlay.js), mirrored for the fade-out.
const FADE_STAGGER_MAX_MS = 900;
const FADE_STAGGER_STEP_MS = 45;
export const fadeStaggerDelays = (count) => {
    const total = Math.min(FADE_STAGGER_MAX_MS, Math.max(0, count - 1) * FADE_STAGGER_STEP_MS);
    return Array.from({ length: count }, (_, index) => (count > 1 ? (index / (count - 1)) * total : 0));
};

// Fades one area's path from its current look to whatever the view styles it as next, once its toggle is
// ready. Meanwhile the path's setStyle only records the look asked for, so hover and restyles carry on
// underneath and the fade ends on the latest one. delayMs holds the fade off that long after it could
// start, so a batch can be staggered (the tile keeps its current look until then).
export const fadeAreaLayer = (layer, regsoCode, delayMs = 0) => {
    if (!layer || typeof layer.setStyle !== 'function') return;
    layer.__areaFade?.finish();
    const baseSetStyle = L.Path.prototype.setStyle;
    const from = pickStyle(layer.options);
    const target = { ...from };
    let changed = false;
    let ready = false;
    let startedAt = null;
    let rafId = null;
    let startTimer = null;
    let done = false;
    let called = false;
    let expireTimer = null;

    const finish = () => {
        if (done) return;
        done = true;
        if (rafId) cancelAnimationFrame(rafId);
        clearTimeout(startTimer);
        clearTimeout(expireTimer);
        delete layer.setStyle;
        layer.__areaFade = null;
        if (called) baseSetStyle.call(layer, target);
    };

    const frame = (now) => {
        rafId = null;
        if (done) return;
        const t = Math.min(1, (now - startedAt) / AREA_TOGGLE_FADE_MS);
        baseSetStyle.call(layer, mixStyle(from, target, easeAreaFade(t)));
        if (t < 1) rafId = requestAnimationFrame(frame);
        else finish();
    };

    const begin = () => {
        startTimer = null;
        if (done) return;
        startedAt = performance.now();
        rafId = requestAnimationFrame(frame);
    };

    const maybeStart = () => {
        if (done || !ready || !changed || startedAt != null || startTimer != null) return;
        if (delayMs > 0) startTimer = setTimeout(begin, delayMs);
        else begin();
    };

    layer.setStyle = function setStyleDuringFade(style) {
        if (style) {
            called = true;
            Object.assign(target, style);
            changed = changed || STYLE_KEYS.some((key) => target[key] !== from[key]);
            // Nothing that shows has changed (yet): pass it on (the drawer's search wraps every area).
            if (!changed) baseSetStyle.call(layer, style);
        }
        maybeStart();
        return this;
    };
    // Nothing changed after all (or the layer went): let go.
    expireTimer = setTimeout(finish, HOLD_FALLBACK_MS + AREA_TOGGLE_FADE_MS + 500);
    layer.__areaFade = { finish };
    whenAreaFadeReady(regsoCode, () => {
        ready = true;
        maybeStart();
        // Still unchanged a fade's length after it could start: this area isn't changing.
        setTimeout(() => {
            if (!changed) finish();
        }, AREA_TOGGLE_FADE_MS);
    });
};

// Every area layer in the group fades to whatever the new filters (or the settle after a reveal) make it.
// With orderedCodes, the layers fade one after another in that order (the reveal order), so the tile that
// lit up first fades its colour first; without it they all fade at once (the drawer's search).
export const fadeAreaLayers = (layerGroup, orderedCodes = null) => {
    if (orderedCodes && orderedCodes.length) {
        const delays = fadeStaggerDelays(orderedCodes.length);
        const delayByCode = new Map(orderedCodes.map((code, index) => [String(code || '').trim(), delays[index]]));
        layerGroup?.eachLayer?.((layer) => {
            const code = String(layer.feature?.properties?.regsokod || '').trim();
            fadeAreaLayer(layer, code, delayByCode.get(code) || 0);
        });
        return;
    }
    layerGroup?.eachLayer?.((layer) => fadeAreaLayer(layer, layer.feature?.properties?.regsokod));
};
